import { z } from "zod";
import { localizedTextSchema } from "./intake-template";

// TES-209 — clinical journey PHASE model. Admin-authored, tenant-scoped named
// phases the patient progresses through ("Lär känna din kropp" → "Bygg din
// grund" → …). Progress is DERIVED from already-tracked patient signals — no
// per-patient phase state is stored. Distinct from the Flows/Journey
// orchestration layer (TES-226), which sequences *content*, not clinical phases.

// The signal that marks a phase step complete. Each maps to a fact derivable
// from existing patient state in the resolver (treatment, forms, labs, …).
// `manual` is a narrative step with no auto-completion — it renders as the
// active/next focus but never auto-checks.
export const PHASE_STEP_SIGNALS = [
  "intake_submitted",
  "consult_completed",
  "labs_analyzed",
  "treatment_started",
  "first_check_in",
  "subscription_active",
  "milestone_1m",
  "milestone_3m",
  "milestone_6m",
  "manual",
] as const;
export const phaseStepSignalSchema = z.enum(PHASE_STEP_SIGNALS);
export type PhaseStepSignal = z.infer<typeof phaseStepSignalSchema>;

export const PHASE_STATUSES = ["draft", "published", "archived"] as const;
export const phaseStatusSchema = z.enum(PHASE_STATUSES);
export type PhaseStatus = z.infer<typeof phaseStatusSchema>;

// One ordered step within a phase.
export const journeyPhaseStepSchema = z.object({
  key: z.string().min(1),
  label: localizedTextSchema,
  completedBy: phaseStepSignalSchema,
});
export type JourneyPhaseStep = z.infer<typeof journeyPhaseStepSchema>;

// Authoring DTO (admin) — the full stored phase definition.
export const journeyPhaseSchema = z.object({
  id: z.string().uuid(),
  key: z.string().min(1),
  emoji: z.string().nullable(),
  title: localizedTextSchema,
  focus: localizedTextSchema,
  steps: z.array(journeyPhaseStepSchema),
  order: z.number().int(),
  status: phaseStatusSchema,
});
export type JourneyPhase = z.infer<typeof journeyPhaseSchema>;

// Create/update payload (admin). `key` is a stable slug; order positions the
// phase in the sequence.
export const upsertJourneyPhaseSchema = z.object({
  key: z
    .string()
    .min(1)
    .regex(/^[a-z0-9_]+$/, "key must be lower_snake_case"),
  emoji: z.string().max(8).nullable().optional(),
  title: localizedTextSchema,
  focus: localizedTextSchema,
  steps: z.array(journeyPhaseStepSchema).default([]),
  order: z.number().int().min(0),
  status: phaseStatusSchema.default("draft"),
});
export type UpsertJourneyPhase = z.infer<typeof upsertJourneyPhaseSchema>;

// ---------------------------------------------------------------------------
// Patient-facing DTO — locale-resolved, with derived completion.
// ---------------------------------------------------------------------------

export const patientPhaseStepSchema = z.object({
  key: z.string(),
  label: z.string(),
  done: z.boolean(),
});
export type PatientPhaseStep = z.infer<typeof patientPhaseStepSchema>;

export const patientPhaseSchema = z.object({
  key: z.string(),
  emoji: z.string().nullable(),
  title: z.string(),
  focus: z.string(),
  steps: z.array(patientPhaseStepSchema),
});
export type PatientPhase = z.infer<typeof patientPhaseSchema>;

// The patient's journey snapshot: where they are + what's next. Null when the
// tenant has no published phases.
export const patientJourneySchema = z.object({
  // 0-based index of the current phase in the published sequence.
  phaseIndex: z.number().int(),
  totalPhases: z.number().int(),
  // 1-based "Steg X av Y" within the current phase.
  stepIndex: z.number().int(),
  totalSteps: z.number().int(),
  current: patientPhaseSchema,
  // The upcoming phase (the "Coming Next" teaser), or null if on the last phase.
  next: patientPhaseSchema.nullable(),
});
export type PatientJourney = z.infer<typeof patientJourneySchema>;
